// ═══════════════════════════════════════════════════════════════════════════
// PanneauMigrations.jsx — Migrations & Déploiements — PesacoreIH
// ═══════════════════════════════════════════════════════════════════════════

import {
  AlertTriangle, CheckCircle, ChevronDown,
  Code2, Database, GitBranch, History,
  Layers, Play, Plus, RefreshCw, RotateCcw,
  Trash2, X, Zap
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';
import usePesacoreIH from '../../stores/usePesacore';

// ─── Constantes ───────────────────────────────────────────────────────────

const MIGRATION_TYPES = [
  { value: 'mongodb-schema',  label: 'MongoDB Schéma',   icon: Database,  desc: 'Ajout/renommage/suppression de champs, index' },
  { value: 'mongodb-data',    label: 'MongoDB Données',  icon: Layers,    desc: 'Transformation et recalcul de documents' },
  { value: 'business-data',   label: 'Données Métier',   icon: Zap,       desc: 'Recalcul scores, réindexation Progression' },
  { value: 'node-script',     label: 'Script Node',      icon: Code2,     desc: 'Script arbitraire avec fonctions up/down' },
  { value: 'sequence',        label: 'Séquence',         icon: GitBranch, desc: 'Enchaînement ordonné de migrations' },
];

const SCHEMA_ACTIONS = ['addField','renameField','removeField','addIndex'];
const BUSINESS_TARGETS = ['reindex-progressions','recalcul-scores'];

const STATUS_CONFIG = {
  pending:    { label: 'En attente',  color: 'text-zinc-400',    bg: 'bg-zinc-800/60',        border: 'border-zinc-700/40'   },
  running:    { label: 'En cours',    color: 'text-sky-400',     bg: 'bg-sky-950/30',         border: 'border-sky-700/40'    },
  deployed:   { label: 'Déployée',    color: 'text-emerald-400', bg: 'bg-emerald-950/30',     border: 'border-emerald-700/40'},
  failed:     { label: 'Échec',       color: 'text-red-400',     bg: 'bg-red-950/30',         border: 'border-red-700/40'    },
  rolledback: { label: 'Annulée',     color: 'text-amber-400',   bg: 'bg-amber-950/30',       border: 'border-amber-700/40'  },
};

// ─── Composants UI ────────────────────────────────────────────────────────

function Badge({ children, color = 'zinc' }) {
  const map = {
    zinc: 'bg-zinc-800 text-zinc-400 border-zinc-700',
    sky:  'bg-sky-950/40 text-sky-300 border-sky-800/40',
    emerald: 'bg-emerald-950/40 text-emerald-300 border-emerald-800/40',
    amber: 'bg-amber-950/40 text-amber-300 border-amber-800/40',
    rose:  'bg-rose-950/40 text-rose-300 border-rose-800/40',
    orange:'bg-orange-950/40 text-orange-300 border-orange-800/40',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-mono ${map[color]}`}>{children}</span>;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return <span className={`text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>;
}

function Feedback({ msg, type, onClear }) {
  if (!msg) return null;
  const s = { success:'bg-emerald-950/30 border-emerald-800/30 text-emerald-300', error:'bg-red-950/30 border-red-800/30 text-red-300', 'dry-run':'bg-amber-950/30 border-amber-800/30 text-amber-300', info:'bg-sky-950/30 border-sky-800/30 text-sky-300' };
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 rounded border text-xs ${s[type]||s.info}`}>
      <span>{type==='dry-run'&&'🧪 DRY-RUN — '}{msg}</span>
      {onClear&&<button onClick={onClear}><X size={12}/></button>}
    </div>
  );
}

// ─── Hook données ─────────────────────────────────────────────────────────

function useMigrationsData() {
  // ── Connexion store/moteur ─────────────────────────────────────────────
  const { dryRunMode, pendingChanges, prepareAction, registerMigration, deployMigration, rollbackMigration } = usePesacoreIH();

  const [migrations, setMigrations] = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [history,    setHistory]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get('/superadmin/migrations');
      setMigrations(r.data.migrations || []);
      setSummary(r.data.summary || null);
      setHistory(r.data.history || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const register = async (data) => {
    // 1. Validation moteur avant envoi backend
    const check = prepareAction?.('MIGRATION_REGISTER', data);
    if (check && !check.valid) throw new Error(check.errors?.join(', ') || 'Rejeté par le moteur');

    const r = await api.post('/superadmin/migrations/register', data);

    // 2. Sync store moteur (pendingMigrations)
    try { registerMigration?.(r.data.migration || data); } catch {}

    await fetchAll();
    return r.data;
  };

  const deploy = async (id, confirmationToken) => {
    // Validation moteur
    const mig   = migrations.find(m => m._id === id || m.id === id);
    const check = prepareAction?.('MIGRATION_DEPLOY', { id, migration: mig });
    if (check && !check.valid) throw new Error(check.errors?.join(', ') || 'Rejeté par le moteur');

    // Backend
    const r = await api.post(`/superadmin/migrations/${id}/deploy`, {
      confirmationToken,
      dryRun: dryRunMode,
    });

    // Sync store
    try { await deployMigration?.(id); } catch {}

    await fetchAll();
    return r.data;
  };

  const rollback = async (id) => {
    const r = await api.post(`/superadmin/migrations/${id}/rollback`);
    // Sync store
    try { await rollbackMigration?.(id); } catch {}
    await fetchAll();
    return r.data;
  };

  const remove = async (id) => {
    const r = await api.delete(`/superadmin/migrations/${id}`);
    await fetchAll();
    return r.data;
  };

  useEffect(() => { fetchAll(); }, []);

  return { migrations, summary, history, loading, error, fetchAll, register, deploy, rollback, remove, dryRunMode, pendingChanges };
}

// ─── Carte Migration ──────────────────────────────────────────────────────

function MigrationCard({ migration, onDeploy, onRollback, onDelete, dryRunMode, allMigrations }) {
  const [expanded,  setExpanded]  = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [token,     setToken]     = useState('');
  const [showToken, setShowToken] = useState(false);
  const [feedback,  setFeedback]  = useState(null);

  const cfg      = STATUS_CONFIG[migration.status] || STATUS_CONFIG.pending;
  const TypeIcon = MIGRATION_TYPES.find(t => t.value === migration.type)?.icon || Code2;
  const deps     = (migration.dependsOn || []).map(id => allMigrations.find(m => m.id === id)).filter(Boolean);
  const depsOk   = deps.every(d => d.status === 'deployed');

  const showFb = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 5000);
  };

  const handleDeploy = async () => {
    if (migration.isDestructive && !dryRunMode && token !== migration.confirmationToken) {
      setShowToken(true);
      return;
    }
    setDeploying(true);
    try {
      const r = await onDeploy(migration.id, token || undefined);
      showFb(r.dryRun ? 'dry-run' : r.success ? 'success' : 'error',
        r.dryRun ? `DRY-RUN : "${migration.name}" validée` :
        r.success ? `Migration déployée en ${r.durationMs}ms` :
        r.error || 'Erreur');
    } finally {
      setDeploying(false);
      setShowToken(false);
      setToken('');
    }
  };

  const handleRollback = async () => {
    if (!window.confirm(`Rollback "${migration.name}" ?`)) return;
    const r = await onRollback(migration.id);
    showFb(r.success ? 'info' : 'error', r.success ? 'Rollback effectué' : r.error);
  };

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${cfg.bg} ${cfg.border}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(v => !v)}>
        <TypeIcon size={14} className="text-zinc-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-zinc-200 truncate">{migration.name}</span>
            <StatusBadge status={migration.status} />
            {migration.isDestructive && <Badge color="rose">destructive</Badge>}
            {migration.source === 'file'  && <Badge color="zinc">fichier</Badge>}
            {migration.modified           && <Badge color="amber">modifié</Badge>}
            {migration.dependsOn?.length > 0 && !depsOk && <Badge color="amber">dépendances</Badge>}
          </div>
          <div className="flex gap-4 mt-0.5 text-[10px] text-zinc-600">
            <span>{MIGRATION_TYPES.find(t => t.value === migration.type)?.label}</span>
            {migration.deployedAt && <span>Déployé {new Date(migration.deployedAt).toLocaleDateString('fr-FR')}</span>}
            {migration.order > 0  && <span>Ordre #{migration.order}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
          {/* Deploy */}
          {['pending','failed','rolledback'].includes(migration.status) && (
            <button
              onClick={handleDeploy}
              disabled={deploying || (!depsOk && deps.length > 0)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs disabled:opacity-30"
            >
              {deploying ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />}
              {dryRunMode ? 'Simuler' : 'Déployer'}
            </button>
          )}
          {/* Rollback */}
          {migration.status === 'deployed' && (
            <button onClick={handleRollback}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-950/30 hover:bg-amber-950/50 border border-amber-700/30 rounded text-amber-300 text-xs">
              <RotateCcw size={12} />
              Rollback
            </button>
          )}
          {/* Delete */}
          {migration.status !== 'deployed' && (
            <button onClick={() => onDelete(migration.id)}
              className="p-1.5 bg-zinc-800 border border-zinc-700 hover:border-red-700/40 rounded text-zinc-600 hover:text-red-400">
              <Trash2 size={12} />
            </button>
          )}
          <ChevronDown size={13} className={`text-zinc-700 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className="px-4 pb-2">
          <Feedback {...feedback} onClear={() => setFeedback(null)} />
        </div>
      )}

      {/* Confirmation token destructive */}
      {showToken && (
        <div className="px-4 pb-3 space-y-2">
          <div className="px-3 py-2 bg-red-950/30 border border-red-800/30 rounded text-red-300 text-xs">
            ⚠ Migration destructive — tapez le token de confirmation
          </div>
          <div className="flex gap-2">
            <input value={token} onChange={e => setToken(e.target.value)}
              className="flex-1 bg-black border border-red-800/40 rounded px-3 py-2 text-xs font-mono text-zinc-200 outline-none focus:border-red-600/50"
              placeholder={migration.confirmationToken} />
            <button onClick={handleDeploy} disabled={token !== migration.confirmationToken}
              className="px-3 py-2 bg-red-700/40 border border-red-600/40 rounded text-red-200 text-xs disabled:opacity-30">
              Confirmer
            </button>
            <button onClick={() => setShowToken(false)}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-400 text-xs">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Détails expandés */}
      {expanded && (
        <div className="border-t border-zinc-800/40 px-4 py-3 space-y-3 bg-black/20">
          {migration.description && (
            <p className="text-xs text-zinc-500 italic">{migration.description}</p>
          )}

          {/* Opération */}
          {Object.keys(migration.operation || {}).length > 0 && (
            <div>
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-1">Opération</p>
              <pre className="text-[10px] text-zinc-400 font-mono bg-black/40 rounded p-2 overflow-auto max-h-32">
                {JSON.stringify(migration.operation, null, 2)}
              </pre>
            </div>
          )}

          {/* Dépendances */}
          {deps.length > 0 && (
            <div>
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-1">Dépendances</p>
              <div className="flex flex-wrap gap-1">
                {deps.map(dep => (
                  <span key={dep.id} className={`text-[10px] font-mono flex items-center gap-1 ${dep.status === 'deployed' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {dep.status === 'deployed' ? <CheckCircle size={10}/> : <AlertTriangle size={10}/>}
                    {dep.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Résultats */}
          {migration.results && (
            <div>
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-1">Résultats</p>
              <pre className="text-[10px] text-emerald-400 font-mono bg-black/40 rounded p-2 overflow-auto max-h-28">
                {JSON.stringify(migration.results, null, 2)}
              </pre>
            </div>
          )}

          {/* Erreur */}
          {migration.error && (
            <div className="px-3 py-2 bg-red-950/20 border border-red-800/20 rounded text-red-300 text-xs font-mono">
              {migration.error}
            </div>
          )}

          {/* Snapshot */}
          {migration.snapshot && (
            <div>
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-1">Snapshot pré-déploiement</p>
              <div className="text-[10px] text-zinc-500 font-mono">
                {migration.snapshot.collectionName} — {migration.snapshot.count} docs — {migration.snapshot.sizeKB} KB
              </div>
            </div>
          )}

          <div className="flex gap-4 text-[10px] text-zinc-700 pt-1">
            <span>ID: {migration.id}</span>
            <span>checksum: {migration.checksum}</span>
            {migration.registeredBy && <span>par: {migration.registeredBy}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Formulaire création ──────────────────────────────────────────────────

function FormCreation({ onSubmit, onClose, allMigrations, dryRunMode }) {
  const [form, setForm] = useState({
    name: '', description: '', type: 'mongodb-schema',
    isDestructive: false, order: 0, dependsOn: [],
    // mongodb-schema
    collection: '', action: 'addField', field: '', newField: '', defaultValue: '',
    indexFieldsRaw: '{ "field": 1 }', indexOptionsRaw: '{}',
    // mongodb-data
    filterRaw: '{}', transformationRaw: '{ "$set": { "field": "value" } }',
    // business-data
    target: 'reindex-progressions',
    // sequence
    stepsRaw: '[]',
  });
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const buildOperation = () => {
    switch (form.type) {
      case 'mongodb-schema': {
        const op = { action: form.action, collection: form.collection };
        if (['addField','renameField','removeField'].includes(form.action)) op.field = form.field;
        if (form.action === 'renameField') op.newField = form.newField;
        if (form.action === 'addField')    op.defaultValue = form.defaultValue || null;
        if (form.action === 'addIndex') {
          op.indexFields  = JSON.parse(form.indexFieldsRaw);
          op.indexOptions = JSON.parse(form.indexOptionsRaw);
        }
        return op;
      }
      case 'mongodb-data':
        return { collection: form.collection, filter: JSON.parse(form.filterRaw), transformation: JSON.parse(form.transformationRaw) };
      case 'business-data':
        return { target: form.target };
      case 'node-script':
        return {};
      case 'sequence':
        return { steps: JSON.parse(form.stepsRaw) };
    }
  };

  const handleSubmit = async () => {
    setErr(null);
    if (!form.name.trim()) return setErr('Nom obligatoire');
    try {
      const operation = buildOperation();
      await onSubmit({ name: form.name, description: form.description, type: form.type, operation, isDestructive: form.isDestructive, order: Number(form.order), dependsOn: form.dependsOn });
      onClose();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="bg-zinc-900/60 border border-orange-800/30 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-black text-orange-300 uppercase tracking-tighter">
          {dryRunMode ? '🧪 ' : ''}Nouvelle Migration
        </h4>
        <button onClick={onClose}><X size={14} className="text-zinc-500 hover:text-white" /></button>
      </div>

      {err && <div className="px-3 py-2 bg-red-950/30 border border-red-800/30 rounded text-red-300 text-xs">{err}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Nom */}
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Nom</label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
            placeholder="ex: add-status-field" />
        </div>

        {/* Type */}
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Type</label>
          <select value={form.type} onChange={e => set('type', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none">
            {MIGRATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>)}
          </select>
        </div>

        {/* Description */}
        <div className="sm:col-span-2">
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Description</label>
          <input value={form.description} onChange={e => set('description', e.target.value)}
            className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
            placeholder="Description optionnelle..." />
        </div>

        {/* Champs selon le type */}
        {['mongodb-schema','mongodb-data'].includes(form.type) && (
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Collection</label>
            <input value={form.collection} onChange={e => set('collection', e.target.value)}
              className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
              placeholder="ex: users" />
          </div>
        )}

        {form.type === 'mongodb-schema' && (
          <>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Action</label>
              <select value={form.action} onChange={e => set('action', e.target.value)}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none">
                {SCHEMA_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            {form.action !== 'addIndex' && (
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Champ</label>
                <input value={form.field} onChange={e => set('field', e.target.value)}
                  className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none" placeholder="ex: status" />
              </div>
            )}
            {form.action === 'renameField' && (
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Nouveau nom</label>
                <input value={form.newField} onChange={e => set('newField', e.target.value)}
                  className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none" placeholder="ex: statusNew" />
              </div>
            )}
            {form.action === 'addField' && (
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Valeur défaut</label>
                <input value={form.defaultValue} onChange={e => set('defaultValue', e.target.value)}
                  className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none" placeholder='ex: "active" ou null' />
              </div>
            )}
          </>
        )}

        {form.type === 'mongodb-data' && (
          <>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Filtre (JSON)</label>
              <textarea value={form.filterRaw} onChange={e => set('filterRaw', e.target.value)} rows={2}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-200 focus:border-orange-600/50 outline-none resize-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Transformation (JSON)</label>
              <textarea value={form.transformationRaw} onChange={e => set('transformationRaw', e.target.value)} rows={3}
                className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-200 focus:border-orange-600/50 outline-none resize-none" />
            </div>
          </>
        )}

        {form.type === 'business-data' && (
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">Cible</label>
            <select value={form.target} onChange={e => set('target', e.target.value)}
              className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none">
              {BUSINESS_TARGETS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        {form.type === 'sequence' && (
          <div className="sm:col-span-2">
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1">
              Étapes (IDs de migrations, JSON array)
            </label>
            <textarea value={form.stepsRaw} onChange={e => set('stepsRaw', e.target.value)} rows={3}
              className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-200 focus:border-orange-600/50 outline-none resize-none"
              placeholder='["1234_migration_a", "1235_migration_b"]' />
          </div>
        )}

        {form.type === 'node-script' && (
          <div className="sm:col-span-2 px-3 py-2 bg-sky-950/20 border border-sky-800/20 rounded text-sky-300 text-xs">
            Un fichier stub <span className="font-mono">/migrations/&lt;id&gt;.js</span> sera créé automatiquement. Implémentez les fonctions <span className="font-mono">up()</span> et <span className="font-mono">down()</span>.
          </div>
        )}

        {/* Options */}
        <div className="sm:col-span-2 flex items-center gap-6 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={form.isDestructive} onChange={e => set('isDestructive', e.target.checked)}
              className="rounded bg-zinc-800 border-zinc-700" />
            Migration destructive
          </label>
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Ordre d'exécution</label>
            <input type="number" value={form.order} onChange={e => set('order', e.target.value)} min={0}
              className="w-16 bg-black border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200 outline-none" />
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button onClick={onClose} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">Annuler</button>
        <button onClick={handleSubmit} disabled={!form.name.trim()}
          className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs font-medium disabled:opacity-30">
          {dryRunMode ? '🧪 Simuler' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ─── Panneau Historique ───────────────────────────────────────────────────

function PanneauHistorique({ history }) {
  if (!history.length) return <div className="text-center py-8 text-zinc-700 text-sm">Aucun déploiement</div>;
  return (
    <div className="bg-zinc-900/30 border border-zinc-800/40 rounded-xl overflow-hidden">
      <div className="divide-y divide-zinc-800/30 max-h-96 overflow-y-auto">
        {history.map((e, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 text-xs hover:bg-zinc-800/20">
            <span className="text-zinc-700 font-mono whitespace-nowrap text-[10px]">
              {new Date(e.deployedAt || e.rollbackAt).toLocaleString('fr-FR')}
            </span>
            <span className={`font-bold uppercase text-[10px] w-20 ${
              e.status === 'deployed'   ? 'text-emerald-400' :
              e.status === 'rollback'   ? 'text-amber-400'   :
              e.status === 'failed'     ? 'text-red-400'     : 'text-zinc-400'
            }`}>{e.status}</span>
            <span className="text-zinc-400 flex-1 truncate">{e.name}</span>
            <span className="text-zinc-700 text-[10px]">{e.type}</span>
            {e.durationMs && <span className="text-zinc-700 text-[10px] ml-auto">{e.durationMs}ms</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Panneau principal ────────────────────────────────────────────────────

export default function PanneauMigrations() {
  const { migrations, summary, history, loading, error, fetchAll, register, deploy, rollback, remove, dryRunMode } = useMigrationsData();
  const [showForm,  setShowForm]  = useState(false);
  const [activeTab, setActiveTab] = useState('migrations');
  const [search,    setSearch]    = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [feedback,  setFeedback]  = useState(null);

  const showFb = (type, msg) => { setFeedback({ type, msg }); setTimeout(() => setFeedback(null), 4000); };

  const filtered = useMemo(() => {
    return migrations.filter(m => {
      const matchS = !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.type.includes(search.toLowerCase());
      const matchF = filterStatus === 'all' || m.status === filterStatus;
      return matchS && matchF;
    });
  }, [migrations, search, filterStatus]);

  const handleRegister = async (data) => {
    try {
      await register(data);
      showFb('success', `Migration "${data.name}" enregistrée`);
    } catch (err) {
      showFb('error', err.response?.data?.error || err.message);
      throw err;
    }
  };

  return (
    <div className="space-y-5 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-orange-300 tracking-tighter">MIGRATIONS & DÉPLOIEMENTS</h2>
          {summary && (
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-0.5">
              {summary.total} total · {summary.pending} en attente · {summary.deployed} déployées · {summary.failed} échecs
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll} disabled={loading}
            className="p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-40">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-700 hover:border-orange-600/40 rounded text-zinc-300 text-xs">
            <Plus size={12} />
            Nouvelle migration
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total',      value: summary.total,      color: 'text-zinc-300'    },
            { label: 'En attente', value: summary.pending,    color: 'text-zinc-400'    },
            { label: 'Déployées',  value: summary.deployed,   color: 'text-emerald-400' },
            { label: 'Échecs',     value: summary.failed,     color: 'text-red-400'     },
          ].map(k => (
            <div key={k.label} className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-3 text-center">
              <div className={`text-2xl font-black ${k.color}`}>{k.value}</div>
              <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-red-950/30 border border-red-800/30 rounded-xl text-red-300 text-xs flex gap-2">
          <AlertTriangle size={14} />{error}
        </div>
      )}

      <Feedback {...(feedback||{})} onClear={() => setFeedback(null)} />

      {/* Formulaire */}
      {showForm && (
        <FormCreation
          onSubmit={handleRegister}
          onClose={() => setShowForm(false)}
          allMigrations={migrations}
          dryRunMode={dryRunMode}
        />
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900/50 border border-zinc-800/60 rounded-xl p-1">
        {[
          { id: 'migrations', label: 'Migrations', icon: Database },
          { id: 'historique', label: 'Historique',  icon: History  },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs transition-all ${
              activeTab === tab.id ? 'bg-orange-950/50 border border-orange-700/40 text-orange-300' : 'text-zinc-600 hover:text-zinc-300'
            }`}>
            <tab.icon size={13} />
            {tab.label}
            {tab.id === 'migrations' && summary?.pending > 0 && (
              <span className="text-[9px] bg-orange-800/60 text-orange-200 px-1.5 rounded-full">{summary.pending}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab : Migrations */}
      {activeTab === 'migrations' && (
        <div className="space-y-4">
          {/* Filtres */}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-40">
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Filtrer..."
                className="w-full pl-3 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-200 focus:border-orange-600/50 outline-none" />
            </div>
            <div className="flex gap-1">
              {['all','pending','deployed','failed','rolledback'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-2.5 py-1.5 rounded border text-[10px] transition-all ${
                    filterStatus === s ? 'bg-orange-950/40 border-orange-700/50 text-orange-300' : 'bg-zinc-900 border-zinc-800 text-zinc-600 hover:text-zinc-400'
                  }`}>
                  {s === 'all' ? 'Toutes' : STATUS_CONFIG[s]?.label || s}
                </button>
              ))}
            </div>
          </div>

          {/* Liste */}
          <div className="space-y-2">
            {loading && migrations.length === 0 ? (
              <div className="text-center py-10 text-zinc-700 text-sm">Chargement...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 border border-zinc-800/40 rounded-xl text-zinc-700 text-sm">
                Aucune migration{filterStatus !== 'all' ? ` avec le statut "${filterStatus}"` : ''}
              </div>
            ) : (
              filtered.map(m => (
                <MigrationCard
                  key={m.id}
                  migration={m}
                  onDeploy={deploy}
                  onRollback={rollback}
                  onDelete={async (id) => {
                    const r = await remove(id);
                    showFb(r.success ? 'success' : 'error', r.success ? 'Migration supprimée' : r.error);
                  }}
                  dryRunMode={dryRunMode}
                  allMigrations={migrations}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Tab : Historique */}
      {activeTab === 'historique' && <PanneauHistorique history={history} />}
    </div>
  );
}